import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { paymentService } from '../services/paymentService';
import { showToast } from '../utils/toast';
import styles from './PaymentModal.module.css';

function PaymentModal({ plan, brandId, onClose, onSuccess }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const { token, onboarding } = useSelector(state => state.user);

  const formatPrice = (plan) => {
    const price = parseInt(plan.price_per_month, 10);
    if (!price) return 'Free';
    const formatted = price.toLocaleString('en-IN');
    return (
      <>
        ₹{formatted}<span className={styles.annually}>/annually</span>
      </>
    );
  };
  const getPrice = (plan) => {
    const price = parseInt(plan.price_per_month, 10);
    const formatted = price.toLocaleString('en-IN');
    return formatted
  }
  const handlePayment = async () => {
    if (!plan || !brandId) {
      setError('Missing plan or brand information. Please try again.');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      // Call the payment initiation API
      const planNameForApi = plan.name;
      const response = await paymentService.initiatePayment(planNameForApi, brandId, token);

      if (response.status === 'success') {
        // Store transaction ID for later verification
        localStorage.setItem('pending_transaction_id', response.transaction_id);
        localStorage.setItem('pending_plan_name', String(planNameForApi));
        
        // Check if user is in onboarding flow
        // NEW API FORMAT: stage is 'pending', 'draft', or 'completed'
        const isOnboarding = onboarding && onboarding.stage && onboarding.stage !== 'completed';
        
        if (isOnboarding) {
          // For onboarding users, store that this is an onboarding payment
          localStorage.setItem('payment_context', 'onboarding');
          console.log('PaymentModal: Onboarding payment detected, storing context');
        } else {
          // For existing users, store regular payment context
          localStorage.setItem('payment_context', 'subscription');
          console.log('PaymentModal: Regular subscription payment detected');
        }
        
        // Submit to PayU gateway
        paymentService.submitToPayU(response.payment_data, response.payment_url);
      } else {
        throw new Error(response.message || 'Payment initiation failed');
      }
    } catch (err) {
      console.error('Payment error:', err);
      
      // Handle specific subscription upgrade error (status 400)
      if (err.status === 400 && err.data) {
        const errorData = err.data;
        
        // Create detailed toast message for subscription upgrade error
        let toastMessage = errorData.error || 'You already have an active subscription. Subscription upgrades require admin approval. Our team has been notified and will contact you shortly.';
        
        // If there's current and requested plan info, add it to the message
        if (errorData.current_subscription && errorData.requested_plan) {
          toastMessage += `\n\nCurrent Plan: ${errorData.current_subscription.plan_name} ($${errorData.current_subscription.price})`;
          toastMessage += `\nRequested Plan: ${errorData.requested_plan.plan_name} ($${errorData.requested_plan.price})`;
          
          // Add start date if available
          if (errorData.current_subscription.start_date) {
            toastMessage += `\nSubscription Start: ${errorData.current_subscription.start_date}`;
          }
        }
        
        // Show error toast
        showToast({
          type: 'error',
          message: toastMessage,
          duration: 8000, // Longer duration for important message
        });
        
        // Also set local error for modal display
        setError(errorData.error || 'You already have an active subscription. Upgrades require admin approval.');
      }
      // Check for specific CORS error
      else if (err.message.includes('CORS') || err.message.includes('fetch')) {
        const errorMsg = 'Network error: Unable to connect to payment service. Please check your connection and try again.';
        setError(errorMsg);
        showToast({
          type: 'error',
          message: errorMsg,
          duration: 5000,
        });
      } else {
        const errorMsg = err.message || 'Failed to initiate payment. Please try again.';
        setError(errorMsg);
        showToast({
          type: 'error',
          message: errorMsg,
          duration: 5000,
        });
      }
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>Confirm Subscription</h2>
          <button 
            className={styles.closeButton} 
            onClick={handleClose}
            disabled={isProcessing}
          >
            ×
          </button>
        </div>
        
        <div className={styles.modalBody}>
          <div className={styles.planDetails}>
            <h3>{plan?.name}</h3>
            <div className={styles.planPrice}>{formatPrice(plan)}</div>
            <p className={styles.planDescription}>{plan?.description}</p>
          </div>

          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}

          <div className={styles.paymentInfo}>
            <p>You will be redirected to PayU's secure payment gateway to complete your subscription.</p>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button 
            className={styles.cancelButton} 
            onClick={handleClose}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button 
            className={styles.payButton} 
            onClick={handlePayment}
            disabled={isProcessing}
          >
            {isProcessing ? 'Processing...' : `Pay ₹${getPrice(plan)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PaymentModal;
